import type { AppContext } from "../../app/app";
import privacyMd from "../../../PRIVACY.md?raw";
import { el } from "../dom";

// Render the in-repo PRIVACY.md. A deliberately small markdown subset (headings,
// paragraphs, unordered lists, bold and links) built as DOM nodes via `el`, so
// there is no innerHTML and no markup-injection surface.
export async function renderPrivacy(ctx: AppContext): Promise<HTMLElement> {
  const root = el("section", {});
  root.append(
    el("button", {
      class: "back",
      text: "← Back",
      on: { click: () => ctx.navigate({ name: "assemble" }) },
    }),
    renderMarkdown(privacyMd),
  );
  return root;
}

function renderMarkdown(md: string): HTMLElement {
  const wrap = el("div", { class: "prose" });
  const lines = md.split("\n");
  let i = 0;
  while (i < lines.length) {
    const line = lines[i]!;
    const heading = /^(#{1,3})\s+(.*)$/.exec(line);
    if (heading) {
      const level = heading[1]!.length;
      const tag = (level === 1 ? "h1" : level === 2 ? "h2" : "h3") as "h1" | "h2" | "h3";
      wrap.append(el(tag, {}, ...inline(heading[2]!)));
      i++;
      continue;
    }
    if (/^\s*-\s+/.test(line)) {
      const list = el("ul", { class: "prose__list" });
      while (i < lines.length && /^\s*-\s+/.test(lines[i]!)) {
        list.append(el("li", {}, ...inline(lines[i]!.replace(/^\s*-\s+/, ""))));
        i++;
      }
      wrap.append(list);
      continue;
    }
    if (line.trim() === "") {
      i++;
      continue;
    }
    // Paragraph: gather consecutive non-blank, non-structural lines.
    const para: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== "" &&
      !/^(#{1,3})\s/.test(lines[i]!) &&
      !/^\s*-\s+/.test(lines[i]!)
    ) {
      para.push(lines[i]!);
      i++;
    }
    wrap.append(el("p", {}, ...inline(para.join(" "))));
  }
  return wrap;
}

// Inline: [text](url) links, **bold**, and *italic*. Everything else is plain
// text (so a stray bracket without a following "(url)" stays literal).
function inline(text: string): Node[] {
  const nodes: Node[] = [];
  const re = /\[([^\]]+)\]\(([^)]+)\)|\*\*([^*]+)\*\*|\*([^*]+)\*/g;
  let last = 0;
  let m: RegExpExecArray | null;
  while ((m = re.exec(text))) {
    if (m.index > last) nodes.push(document.createTextNode(text.slice(last, m.index)));
    if (m[1] !== undefined && m[2] !== undefined) {
      nodes.push(
        el("a", {
          href: m[2],
          text: m[1],
          attrs: { target: "_blank", rel: "noopener noreferrer" },
        }),
      );
    } else if (m[3] !== undefined) {
      nodes.push(el("strong", { text: m[3] }));
    } else if (m[4] !== undefined) {
      nodes.push(el("em", { text: m[4] }));
    }
    last = re.lastIndex;
  }
  if (last < text.length) nodes.push(document.createTextNode(text.slice(last)));
  return nodes;
}
